import React, { useEffect, useState, useRef } from 'react';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { atom_tourNotifications } from '@app/shared/state/notification.atom';
import { atom_tourStage } from '@app/shared/state/loading.atom';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
import { useNavigationStepTracker } from '@app/shared/hooks/useNavigationStepTracker';
import { useRouteTracker } from '@app/shared/hooks/useRouteTracker';
// DISABLED FOR PROD — re-enable for next release: guide-route navigation feature
// import { useGuideStepTracker } from '@app/shared/hooks/useGuideStepTracker';
import { useRenderTracker } from '@app/shared/debug';

// Haversine distance in meters
function calculateDistance(
  c1: { lat: number; lng: number },
  c2: { lat: number; lng: number }
): number {
  const R = 6371e3;
  const lat1 = (c1.lat * Math.PI) / 180;
  const lat2 = (c2.lat * Math.PI) / 180;
  const dLat = ((c2.lat - c1.lat) * Math.PI) / 180;
  const dLng = ((c2.lng - c1.lng) * Math.PI) / 180;

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const NOTIFICATION_CHECK_THRESHOLD = 30; // Check notification triggers if user moved > 30 meters
const ROUTE_CHECK_THRESHOLD = 10; // Check route step progression if user moved > 10 meters
const UPDATE_INTERVAL = 4000; // Update every 4 seconds
const STATIONARY_SPEED_THRESHOLD = 0.3; // m/s — below this = stationary
const STATIONARY_READINGS_TO_IDLE = 2; // 2 consecutive stationary readings (8s) → kill GPS
const WAKE_CHECK_INTERVAL = 3000; // Sample accelerometer every 3s
const WAKE_ACCEL_THRESHOLD = 1.2; // m/s² — XY only, footstep impact produces ~1-3, phone on table ~0
const WAKE_SAMPLE_DURATION = 1000; // Collect readings for 1s per sample
const WAKE_CONSECUTIVE_HITS = 3; // Need 3 consecutive positive samples (~9-12s) before waking GPS

export const GeolocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useRenderTracker('GeolocationProvider');
  const setGeolocation = useSetRecoilState(atom_geolocationState);
  const [watchId, setWatchId] = useState<number | null>(null);
  const wakeHitsRef = useRef(0);

  const hasWrittenToLocalStorageRef = useRef(false);
  const lastCheckedPositionRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastRouteCheckPositionRef = useRef<{ lat: number; lng: number } | null>(null);

  // GPS lifecycle state
  const gpsStateRef = useRef<'moving' | 'idle'>('moving');
  const stationaryCountRef = useRef(0);
  const wakeIntervalRef = useRef<number | null>(null);
  const currentWatchIdRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef(0);
  const lastPositionRef = useRef<{ lat: number; lng: number; speed: number | null } | null>(null);

  // Use the centralized notification trigger hook
  const notifications = useRecoilValue(atom_tourNotifications);
  const tourStage = useRecoilValue(atom_tourStage);
  const { fireTrigger } = useNotificationTrigger();

  // Use the navigation step tracker hook
  const { checkStepProgression } = useNavigationStepTracker();
  const { checkRouteProgress } = useRouteTracker();
  // DISABLED FOR PROD — re-enable for next release: guide-route navigation feature
  // const { checkGuideStepProgression } = useGuideStepTracker();

  // Keep refs updated to latest callbacks
  const fireTriggerRef = useRef(fireTrigger);
  fireTriggerRef.current = fireTrigger;
  const checkStepProgressionRef = useRef(checkStepProgression);
  checkStepProgressionRef.current = checkStepProgression;
  const checkRouteProgressRef = useRef(checkRouteProgress);
  checkRouteProgressRef.current = checkRouteProgress;
  // DISABLED FOR PROD — re-enable for next release: guide-route navigation feature
  // const checkGuideStepProgressionRef = useRef(checkGuideStepProgression);
  // checkGuideStepProgressionRef.current = checkGuideStepProgression;

  // Keep ref updated to latest tourStage (for use inside watchPosition callback)
  const tourStageRef = useRef(tourStage);
  tourStageRef.current = tourStage;

  // Ref to hold startWatchPosition so wake detector can call it
  const startWatchPositionRef = useRef<(() => void) | null>(null);

  // Check location-based notification triggers
  const checkLocationTriggers = (userLocation: { lat: number; lng: number }) => {
    // Only trigger notifications when on the map stage
    if (tourStageRef.current !== 'map') {
      return;
    }
    // Use the centralized fireTrigger which handles state correctly via snapshot
    fireTriggerRef.current('location', { location: userLocation });
  };

  // Keep ref updated to latest checkLocationTriggers (for use inside watchPosition callback)
  const checkLocationTriggersRef = useRef(checkLocationTriggers);
  checkLocationTriggersRef.current = checkLocationTriggers;

  // Track if we've done the initial check after tourStage became 'map'
  const hasCheckedAfterMapStageRef = useRef(false);

  // Check triggers when tourStage becomes 'map' (position may have arrived earlier)
  useEffect(() => {
    if (tourStage !== 'map') {
      // Reset when we leave the map stage so it triggers again next time
      hasCheckedAfterMapStageRef.current = false;
      return;
    }

    // Safety check for notifications array
    if (!notifications || !Array.isArray(notifications)) return;

    const hasPosition = lastCheckedPositionRef.current !== null;
    const hasLocationNotifications = notifications.some(n => n.triggerType === 'location');

    // If tourStage is 'map', we have location notifications, a position, and haven't checked yet
    if (hasLocationNotifications && hasPosition && !hasCheckedAfterMapStageRef.current) {
      hasCheckedAfterMapStageRef.current = true;
      checkLocationTriggersRef.current(lastCheckedPositionRef.current!);
    }
  }, [tourStage, notifications]);

  // --- Accelerometer wake detector: sample every 1s, require consecutive hits ---
  const startWakeDetector = () => {
    if (wakeIntervalRef.current !== null) return; // Already running
    wakeHitsRef.current = 0;

    wakeIntervalRef.current = window.setInterval(() => {
      const readings: number[] = [];

      const handler = (event: DeviceMotionEvent) => {
        const acc = event.acceleration;
        if (acc && acc.x != null && acc.y != null) {
          const magnitude = Math.sqrt(acc.x ** 2 + acc.y ** 2); // XY only, ignore Z
          readings.push(magnitude);
        }
      };

      window.addEventListener('devicemotion', handler);

      // Collect for 1 second (~60 readings at 60Hz) then evaluate
      window.setTimeout(() => {
        window.removeEventListener('devicemotion', handler);

        const maxAccel = readings.length > 0 ? Math.max(...readings) : 0;
        if (maxAccel > WAKE_ACCEL_THRESHOLD) {
          wakeHitsRef.current++;
        } else {
          wakeHitsRef.current = 0; // Reset — must be consecutive
        }
        // Only wake after sustained motion (3+ consecutive positive samples)
        if (wakeHitsRef.current >= WAKE_CONSECUTIVE_HITS) {
          stopWakeDetector();
          gpsStateRef.current = 'moving';
          stationaryCountRef.current = 0;
          wakeHitsRef.current = 0;
          startWatchPositionRef.current?.();
        }
      }, WAKE_SAMPLE_DURATION);
    }, WAKE_CHECK_INTERVAL);
  };

  const stopWakeDetector = () => {
    if (wakeIntervalRef.current !== null) {
      clearInterval(wakeIntervalRef.current);
      wakeIntervalRef.current = null;
    }
  };

  // --- Transition to IDLE: kill GPS, start wake detector ---
  const transitionToIdle = () => {
    if (currentWatchIdRef.current !== null) {
      navigator.geolocation.clearWatch(currentWatchIdRef.current);
      currentWatchIdRef.current = null;
      setWatchId(null);
    }
    gpsStateRef.current = 'idle';

    startWakeDetector();
  };

  // Request geolocation when provider mounts (map is shown)
  useEffect(() => {
    const startWatchPosition = () => {
      let lastUpdateTime = lastUpdateTimeRef.current;
      let lastPosition = lastPositionRef.current;

      const id = navigator.geolocation.watchPosition(
        (position) => {
          const now = Date.now();
          const speed = position.coords.speed;
          const isStationary = speed === null || speed < STATIONARY_SPEED_THRESHOLD;

          // Throttle: single 4s interval for all updates
          if (now - lastUpdateTime < UPDATE_INTERVAL) {
            return;
          }
          lastUpdateTime = now;
          lastUpdateTimeRef.current = now;

          // Track consecutive stationary readings
          if (isStationary) {
            stationaryCountRef.current++;
          } else {
            stationaryCountRef.current = 0;
          }

          // Check if we should transition to IDLE
          if (stationaryCountRef.current >= STATIONARY_READINGS_TO_IDLE) {
            transitionToIdle();
            return;
          }

          const geoData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            speed: position.coords.speed,
            timestamp: position.timestamp,
            error: null,
            isLoading: false,
          };

          // Only update atom if position meaningfully changed (avoids jitter re-renders)
          const accuracy = position.coords.accuracy || 999;
          const POSITION_MIN_DELTA = Math.max(3, accuracy * 0.15); // At least 3m, or 15% of accuracy
          if (lastPosition) {
            const delta = calculateDistance(
              { lat: lastPosition.lat, lng: lastPosition.lng },
              { lat: position.coords.latitude, lng: position.coords.longitude }
            );
            if (delta < POSITION_MIN_DELTA) {
              return; // Skip - movement is within noise range of current accuracy
            }
          }
          lastPosition = { lat: position.coords.latitude, lng: position.coords.longitude, speed: position.coords.speed };
          lastPositionRef.current = lastPosition;
          setGeolocation(geoData);

          // Check location-based triggers
          const currentLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
          const lastChecked = lastCheckedPositionRef.current;
          const lastRouteCheck = lastRouteCheckPositionRef.current;

          // Check notification triggers (30m threshold)
          if (!lastChecked) {
            lastCheckedPositionRef.current = currentLocation;
            checkLocationTriggersRef.current(currentLocation);
          } else {
            const distanceMoved = calculateDistance(lastChecked, currentLocation);
            if (distanceMoved > NOTIFICATION_CHECK_THRESHOLD) {
              lastCheckedPositionRef.current = currentLocation;
              checkLocationTriggersRef.current(currentLocation);
            }
          }

          // Check route step progression + route tracker (10m threshold)
          if (!lastRouteCheck) {
            lastRouteCheckPositionRef.current = currentLocation;
            checkStepProgressionRef.current(currentLocation);
            checkRouteProgressRef.current(currentLocation);
            // DISABLED FOR PROD — re-enable for next release: guide-route navigation feature
            // checkGuideStepProgressionRef.current(currentLocation);
          } else {
            const routeDistanceMoved = calculateDistance(lastRouteCheck, currentLocation);
            if (routeDistanceMoved > ROUTE_CHECK_THRESHOLD) {
              lastRouteCheckPositionRef.current = currentLocation;
              checkStepProgressionRef.current(currentLocation);
              checkRouteProgressRef.current(currentLocation);
              // DISABLED FOR PROD — re-enable for next release: guide-route navigation feature
              // checkGuideStepProgressionRef.current(currentLocation);
            }
          }

          // Write to localStorage ONCE on first successful geolocation
          if (!hasWrittenToLocalStorageRef.current) {
            try {
              localStorage.setItem('last_known_geolocation', JSON.stringify({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                timestamp: position.timestamp,
              }));
              hasWrittenToLocalStorageRef.current = true;
            } catch (err) {
            }
          }
        },
        (error) => {
          let errorMessage = error.message;
          if (error.code === 1) {
            errorMessage = 'Location permission denied. Please enable location in browser settings.';
          }
          // Preserve last known position — iOS Safari fires transient errors
          // (often code 1) even when permission is granted and a fix is valid.
          setGeolocation((prev) => ({
            ...prev,
            error: {
              code: error.code,
              message: errorMessage,
            },
            isLoading: false,
          }));
        },
        {
          enableHighAccuracy: true,
          maximumAge: 3500, // Allow 3.5s cached positions (aligns with 4s update interval)
          timeout: 120000, // Increased for Samsung A23 GPS cold start
        }
      );
      currentWatchIdRef.current = id;
      setWatchId(id);
    };

    // Store ref so wake detector can call it
    startWatchPositionRef.current = startWatchPosition;

    const requestGeolocation = async () => {
      // Check if geolocation is supported
      if (!('geolocation' in navigator)) {
        setGeolocation({
          latitude: null,
          longitude: null,
          accuracy: null,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null,
          timestamp: null,
          error: {
            code: 0,
            message: 'Geolocation not supported',
          },
          isLoading: false,
        });
        return;
      }

      // Check HTTPS requirement
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        console.error('[GeolocationProvider] Geolocation requires HTTPS');
        setGeolocation({
          latitude: null,
          longitude: null,
          accuracy: null,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null,
          timestamp: null,
          error: {
            code: 0,
            message: 'Geolocation requires HTTPS',
          },
          isLoading: false,
        });
        return;
      }

      // Check current permission state (if Permissions API is available)
      if ('permissions' in navigator) {
        try {
          const permission = await navigator.permissions.query({ name: 'geolocation' });

          if (permission.state === 'denied') {
            setGeolocation({
              latitude: null,
              longitude: null,
              accuracy: null,
              altitude: null,
              altitudeAccuracy: null,
              heading: null,
              speed: null,
              timestamp: null,
              error: {
                code: 1,
                message: 'Location permission denied. Please reset in browser settings.',
              },
              isLoading: false,
            });
            return;
          }
        } catch (err) {
          console.warn('[GeolocationProvider] Permissions API not available:', err);
        }
      }

      startWatchPosition();
    };

    requestGeolocation();

    // Cleanup function
    return () => {
      if (currentWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(currentWatchIdRef.current);
      }
      stopWakeDetector();
    };
  }, [setGeolocation]); // Only run once on mount

  return <>{children}</>;
};
